import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import type { TFunction } from "i18next";
import * as React from "react";
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { cn } from "@/common/helpers/utils";
import { Label } from "@/components/ui/label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext>
  );
};

const useFormField = () => {
  const fieldContext = React.use(FormFieldContext);
  const itemContext = React.use(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = ({ className, ref, ...props }: React.ComponentProps<"div">) => {
  const id = React.useId();

  return (
    <FormItemContext value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext>
  );
};
FormItem.displayName = "FormItem";

interface FormLabelProps extends React.ComponentProps<typeof LabelPrimitive.Root> {
  required?: boolean;
  showError?: boolean;
}

const FormLabel = ({ children, className, required = false, showError = true, ref, ...props }: FormLabelProps) => {
  const { error, formItemId } = useFormField();

  return (
    <Label ref={ref} className={cn(showError && error && "text-error", className)} htmlFor={formItemId} {...props}>
      {children}
      {required ? (
        <span aria-hidden="true" className="text-error ml-1">
          *
        </span>
      ) : null}
    </Label>
  );
};
FormLabel.displayName = "FormLabel";

interface FormControlProps extends React.ComponentProps<typeof Slot> {
  showError?: boolean;
}

const FormControl = ({ showError = true, ref, ...props }: FormControlProps) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  const hasVisibleError = showError && !!error;

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!hasVisibleError ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={hasVisibleError}
      {...props}
    />
  );
};
FormControl.displayName = "FormControl";

const FormDescription = ({ className, ref, ...props }: React.ComponentProps<"p">) => {
  const { formDescriptionId } = useFormField();

  return (
    <p ref={ref} id={formDescriptionId} className={cn("text-muted-foreground text-[0.8rem]", className)} {...props} />
  );
};
FormDescription.displayName = "FormDescription";

interface FormMessageProps extends React.ComponentProps<"p"> {
  showError?: boolean;
}

const FormMessage = ({ className, children, showError = true, ref, ...props }: FormMessageProps) => {
  const { error, formMessageId } = useFormField();
  const { t } = useTranslation();
  const rawBody = showError && error ? String(error?.message) : children;
  const body = typeof rawBody === "string" ? translateFormMessage(t, rawBody) : rawBody;

  if (!body) {
    return null;
  }

  return (
    <p ref={ref} id={formMessageId} className={cn("text-error text-[0.8rem] font-medium", className)} {...props}>
      {body}
    </p>
  );
};
FormMessage.displayName = "FormMessage";

// Messages may be plain i18n keys ("common:Foo") or JSON-encoded `{ key, options }` descriptors —
// the latter lets schemas pass interpolation params (e.g. dynamic date formats) without translating eagerly.
function translateFormMessage(t: TFunction, message: string): string {
  if (message.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(message);
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        "key" in parsed &&
        typeof (parsed as { key: unknown }).key === "string"
      ) {
        const { key, options } = parsed as { key: string; options?: Record<string, string> };
        return t(key, options);
      }
    } catch {
      // not a descriptor — fall through to plain key translation
    }
  }
  return t(message);
}

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
